import React, {FC, useEffect, useLayoutEffect, useState} from 'react';
import {Search} from '@epic-ui/icons';
import Box from '@material-ui/core/Box';
import {withStyles} from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import {useTheme} from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import List from './List';
import Trend from '../trend/Trend';
import ListSkeleton from './ListSkeleton';
import {UpdateButton} from '../update-button';
import AutocompleteList from './AutocompleteList';
import Ellipsis from '../../components/common/Ellipsis';
import {VirtualizedListbox} from './VirtualizedListbox';
import {GlobalSelectButton} from '../global-select-button';
import {CategorizedDropdown} from '../categorized-dropdown';

import {Option} from '../../types';
import {withLocalization} from 'epic-localization/src/context/LocalizationContext';

export enum Category {
    SelectOptions = 'selectOptions',
    RecentGamesOptions = 'recent10GamesOptions',
    Top10GamesOptions = 'top10GamesOptions',
    Top10TrendingOptions = 'top10TrendingOptions'
}

const categoryTabOrder = [
    Category.SelectOptions,
    Category.RecentGamesOptions,
    Category.Top10GamesOptions,
    Category.Top10TrendingOptions
];

export const CODES = {
    [Category.SelectOptions]: 'epic.affiliate.fortnite_games.game_select.select_games',
    [Category.RecentGamesOptions]: 'epic.affiliate.fortnite_games.game_select.recently_published',
    [Category.Top10GamesOptions]: 'epic.affiliate.fortnite_games.game_select.my_top_10_games',
    [Category.Top10TrendingOptions]: 'epic.affiliate.fortnite_games.game_select.my_top_10_trending',
    update: 'epic.affiliate.fortnite_games.update_count',
    customSelection: 'epic.affiliate.fortnite_games.game_select.custom_selection',
    searchPlaceholder: 'epic.affiliate.fortnite_games.game_select.search_placeholder',
    searchResults: 'epic.affiliate.fortnite_games.game_select.search_results',
    selected: 'epic.affiliate.fortnite_games.selected',
    loading: 'epic.affiliate.common.loading',
    label: 'epic.affiliate.fortnite_games.game_select.select_all',
    noOptions: 'epic.affiliate.fortnite_games.game_select.no_options',
    myGames: 'epic.affiliate.fortnite_games.game_select.my_games'
};

export interface GameSelectProps {
    loading?: boolean;
    selectOptions: Option[];
    recent10GamesOptions: Option[];
    top10GamesOptions: Option[];
    top10TrendingOptions: Option[];
    value: {
        category: Category;
        options: Option[];
    };
    onChange: (category: Category, options: Option[]) => void;

    getMessage: (code: string, args?: string[] | number[]) => string;
}

export const ActiveUsers = withStyles(theme => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        '&> svg': {
            fontSize: '1rem'
        },
        '& > span': {
            lineHeight: '1rem',
            paddingLeft: theme.spacing(2)
        },
        color: theme.palette.text.secondary
    }
}))(Typography);

const GameSelect: FC<GameSelectProps> = ({
    loading,
    selectOptions,
    recent10GamesOptions,
    top10GamesOptions,
    top10TrendingOptions,
    value,
    onChange,
    getMessage
}: GameSelectProps) => {
    const optionsData = {
        selectOptions,
        recent10GamesOptions,
        top10GamesOptions,
        top10TrendingOptions
    };

    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
    const [isChanged, setIsChanged] = useState(false);
    const [activeCategory, setActiveCategory] = useState(() =>
        categoryTabOrder.indexOf(value.category)
    );

    const theme = useTheme();
    const isSmBreakpoint = useMediaQuery(theme.breakpoints.up('sm'));

    const [internalValue, setInternalValue] = useState({
        category: Category.SelectOptions,
        options: []
    });

    const {category, options} = internalValue;

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        setActiveCategory(categoryTabOrder.indexOf(category));
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setIsChanged(false);
    };

    const handleChange = (eventCategory: Category) => (_event, value: Option[]) => {
        setInternalValue({category: eventCategory, options: value});
        setIsChanged(true);
    };

    const handleCategoryChange = (_event, index) => {
        const selectedCategory = categoryTabOrder[index];

        setActiveCategory(index);
        setIsChanged(true);

        // "Select Options" category should be with unchecked list by default
        if (selectedCategory === Category.SelectOptions) {
            setInternalValue({category: selectedCategory, options: []});
        } else {
            setInternalValue({
                category: selectedCategory,
                options: optionsData[selectedCategory]
            });
        }
    };

    const handleUpdate = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        onChange(internalValue.category, [...internalValue.options]);

        handleClose();
    };

    const open = Boolean(anchorEl);
    const id = open ? 'game-select' : undefined;
    const isCustomSelection =
        category === Category.SelectOptions || options.length !== optionsData[category].length;
    const placeholder = isCustomSelection
        ? getMessage(CODES.customSelection, [options.length])
        : getMessage(CODES[category]);

    useLayoutEffect(() => {
        setInternalValue(value);
    }, [value]);

    useEffect(() => {
        if (!open && internalValue !== value) {
            setInternalValue(value);
        }
    }, [open]);

    const updateButtonSize = isSmBreakpoint ? 'large' : 'medium';

    return (
        <>
            <GlobalSelectButton
                open={open}
                onClick={handleClick}
                startAdornment={<Search />}
                endAdornment={
                    isChanged && internalValue.options.length ? (
                        <UpdateButton size={updateButtonSize} onClick={handleUpdate}>
                            {getMessage(CODES.update, [internalValue.options.length])}
                        </UpdateButton>
                    ) : null
                }>
                <Ellipsis>{placeholder}</Ellipsis>
            </GlobalSelectButton>
            <CategorizedDropdown
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                value={activeCategory}
                onChange={handleCategoryChange}
                ContentBoxProps={{
                    width: '100%',
                    height: '100%',
                    overflow: 'auto',
                    maxHeight: 460
                }}>
                <CategorizedDropdown.Category label={getMessage(CODES[Category.SelectOptions])}>
                    {loading ? (
                        <ListSkeleton itemsCount={8}>
                            <Skeleton width="100%" height={40} variant="rect" />
                            <Box clone mt={2}>
                                <Skeleton width="30%" height={12} variant="rect" />
                            </Box>
                        </ListSkeleton>
                    ) : (
                        <AutocompleteList
                            options={selectOptions}
                            value={options}
                            resultsLabel={getMessage(CODES.searchResults)}
                            placeholder={getMessage(CODES.searchPlaceholder)}
                            ListboxComponent={VirtualizedListbox}
                            noOptionsText={getMessage(CODES.noOptions)}
                            onChange={handleChange(Category.SelectOptions)}
                        />
                    )}
                </CategorizedDropdown.Category>

                <CategorizedDropdown.Category
                    label={getMessage(CODES[Category.RecentGamesOptions])}>
                    {loading ? (
                        <ListSkeleton />
                    ) : (
                        <List
                            options={recent10GamesOptions}
                            value={options}
                            label={getMessage(CODES.label)}
                            onChange={handleChange(Category.RecentGamesOptions)}
                        />
                    )}
                </CategorizedDropdown.Category>

                <CategorizedDropdown.Category label={getMessage(CODES[Category.Top10GamesOptions])}>
                    {loading ? (
                        <ListSkeleton />
                    ) : (
                        <List
                            options={top10GamesOptions}
                            value={options}
                            label={getMessage(CODES.label)}
                            onChange={handleChange(Category.Top10GamesOptions)}
                            overrideMultipleOptionProps={({secondaryText}) => ({
                                secondaryText: undefined,
                                children: (
                                    <ActiveUsers variant="caption" color="textSecondary">
                                        <PersonIcon fontSize="small" />
                                        <span>{secondaryText}</span>
                                    </ActiveUsers>
                                )
                            })}
                        />
                    )}
                </CategorizedDropdown.Category>

                <CategorizedDropdown.Category
                    label={getMessage(CODES[Category.Top10TrendingOptions])}>
                    {loading ? (
                        <ListSkeleton />
                    ) : (
                        <List
                            options={top10TrendingOptions}
                            value={options}
                            label={getMessage(CODES.label)}
                            onChange={handleChange(Category.Top10TrendingOptions)}
                            overrideMultipleOptionProps={({secondaryText}) => ({
                                secondaryText: undefined,
                                children: <Trend value={+secondaryText} />
                            })}
                        />
                    )}
                </CategorizedDropdown.Category>
            </CategorizedDropdown>
        </>
    );
};

export default withLocalization(GameSelect);
